// src/components/wholesaler/ProductCatalog.js
import { useState } from 'react';
import { ShoppingCart, Plus, Minus, Package } from 'lucide-react';

export function ProductCatalog({ products, onAddToCart, userTier, tierDiscount }) {
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">Wholesale Products</h2>
          {tierDiscount > 0 && (
            <p className="text-sm text-green-600 mt-1">
              Your {userTier} tier includes a {tierDiscount}% discount on all products
            </p>
          )}
        </div>
      </div>

      {products.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Package size={48} className="text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-600">No products available</h3>
          <p className="text-gray-500">Check back soon for new wholesale products.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map(product => (
            <ProductCard 
              key={product.id} 
              product={product} 
              onAddToCart={onAddToCart}
              showOriginalPrice={tierDiscount > 0}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ProductCard({ product, onAddToCart, showOriginalPrice }) {
  const [quantity, setQuantity] = useState(product.minQuantity || 1);

  const handleAddToCart = () => {
    if (quantity >= (product.minQuantity || 1)) {
      onAddToCart(product, quantity);
      // Reset to min quantity after adding
      setQuantity(product.minQuantity || 1);
    }
  };

  const incrementQuantity = () => {
    setQuantity(q => q + 1);
  };

  const decrementQuantity = () => {
    setQuantity(q => Math.max(product.minQuantity || 1, q - 1));
  };

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden hover:shadow-lg transition-shadow">
      {product.image && (
        <div className="aspect-video bg-gray-100 overflow-hidden">
          <img 
            src={product.image} 
            alt={product.name} 
            className="w-full h-full object-cover"
          />
        </div>
      )}
      
      <div className="p-5">
        <h3 className="font-bold text-lg mb-1">{product.name}</h3>
        {product.description && (
          <p className="text-gray-600 text-sm mb-3">{product.description}</p>
        )}

        <div className="flex items-baseline gap-2 mb-2">
          <span className="text-2xl font-bold text-green-600">
            ${product.wholesalePrice.toFixed(2)}
          </span>
          {showOriginalPrice && product.originalWholesalePrice !== product.wholesalePrice && (
            <span className="text-sm text-gray-400 line-through">
              ${product.originalWholesalePrice.toFixed(2)}
            </span>
          )}
          <span className="text-sm text-gray-500">wholesale</span>
        </div>

        {product.retailPrice && (
          <p className="text-sm text-gray-500 mb-3">
            Retail: ${product.retailPrice.toFixed(2)}
            <span className="text-green-600 ml-2">
              ({Math.round((1 - product.wholesalePrice / product.retailPrice) * 100)}% margin)
            </span>
          </p>
        )}

        {product.minQuantity > 1 && (
          <p className="text-xs text-gray-500 mb-3">
            Minimum order: {product.minQuantity} units
          </p>
        )}

        <div className="flex items-center gap-2">
          <div className="flex items-center border rounded-lg">
            <button
              onClick={decrementQuantity}
              className="px-3 py-2 hover:bg-gray-100 rounded-l-lg"
            >
              <Minus size={16} />
            </button>
            <input
              type="number"
              min={product.minQuantity || 1}
              value={quantity}
              onChange={(e) => setQuantity(Math.max(product.minQuantity || 1, parseInt(e.target.value) || 1))}
              className="w-16 text-center py-2 border-x"
            />
            <button
              onClick={incrementQuantity}
              className="px-3 py-2 hover:bg-gray-100 rounded-r-lg"
            >
              <Plus size={16} />
            </button>
          </div>

          <button
            onClick={handleAddToCart}
            className="flex-1 bg-black text-white py-2 px-4 rounded-lg hover:bg-gray-800 flex items-center justify-center gap-2"
          >
            <ShoppingCart size={18} />
            Add
          </button>
        </div>

        <p className="text-sm text-gray-600 text-right mt-2">
          Subtotal: ${(product.wholesalePrice * quantity).toFixed(2)}
        </p>
      </div>
    </div>
  );
}
